import { useState } from "react";
import { VIEW_TYPES, DENSITIES } from "@/lib/pageViews";
import { ColumnManager } from "./ColumnManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Bookmark, ChevronDown, Rows, SlidersHorizontal, Trash2, Check, Users2, User } from "lucide-react";
import { cn } from "@/lib/utils";

export function ViewToolbar({ pageDef, pv, showColumns = true }) {
  const { state, update, views, activeViewId, applyView, saveView, deleteView, resetView, isAdmin } = pv;
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState("");
  const [shared, setShared] = useState(false);

  const personal = views.filter((v) => !v.is_shared);
  const sharedViews = views.filter((v) => v.is_shared);
  const activeName = views.find((v) => v.id === activeViewId)?.name;

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="view-toolbar">
      {/* view type switcher */}
      <div className="flex rounded-lg border border-border p-0.5">
        {VIEW_TYPES.map((v) => {
          const Icon = v.icon; const active = state.view === v.key;
          return (
            <button key={v.key} onClick={() => update({ view: v.key })} title={v.label} data-testid={`view-${v.key}`}
              className={cn("flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors",
                active ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground")}>
              <Icon className="h-4 w-4" /><span className="hidden lg:inline">{v.label}</span>
            </button>
          );
        })}
      </div>

      {/* density */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="gap-1.5" data-testid="density-trigger">
            <Rows className="h-4 w-4" /> <span className="hidden sm:inline">Density</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {DENSITIES.map((d) => (
            <DropdownMenuItem key={d.key} onClick={() => update({ density: d.key })} data-testid={`density-${d.key}`}
              className={cn(state.density === d.key && "text-accent")}>
              {state.density === d.key && <Check className="mr-2 h-4 w-4" />} {d.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {showColumns && <ColumnManager pageDef={pageDef} state={state} update={update} />}

      {/* saved views */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="gap-1.5" data-testid="saved-views-trigger">
            <Bookmark className="h-4 w-4" />
            <span className="max-w-[120px] truncate">{activeName || "Saved views"}</span>
            <ChevronDown className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          <DropdownMenuItem onClick={() => setSaveOpen(true)} data-testid="save-view-open">
            <Bookmark className="mr-2 h-4 w-4" /> Save current view…
          </DropdownMenuItem>
          <DropdownMenuItem onClick={resetView} data-testid="reset-view">
            <SlidersHorizontal className="mr-2 h-4 w-4" /> Reset to default
          </DropdownMenuItem>
          {personal.length > 0 && <><DropdownMenuSeparator /><DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">Personal</DropdownMenuLabel></>}
          {personal.map((v) => (
            <DropdownMenuItem key={v.id} className="group justify-between" onClick={() => applyView(v)} data-testid={`view-item-${v.id}`}>
              <span className="flex items-center gap-2 truncate"><User className="h-3.5 w-3.5 text-muted-foreground" /> {v.name}</span>
              <button onClick={(e) => { e.stopPropagation(); deleteView(v.id); }} className="text-muted-foreground opacity-0 group-hover:opacity-100" data-testid={`delete-view-${v.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuItem>
          ))}
          {sharedViews.length > 0 && <><DropdownMenuSeparator /><DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">Shared</DropdownMenuLabel></>}
          {sharedViews.map((v) => (
            <DropdownMenuItem key={v.id} className="group justify-between" onClick={() => applyView(v)} data-testid={`view-item-${v.id}`}>
              <span className="flex items-center gap-2 truncate"><Users2 className="h-3.5 w-3.5 text-muted-foreground" /> {v.name}</span>
              {isAdmin && (
                <button onClick={(e) => { e.stopPropagation(); deleteView(v.id); }} className="text-muted-foreground opacity-0 group-hover:opacity-100" data-testid={`delete-view-${v.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-sm" data-testid="save-view-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Save view</DialogTitle>
            <DialogDescription>Saves your current filters, sorting, columns, density and view type.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">View name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Active writers" className="mt-1" data-testid="save-view-name" />
            </div>
            {isAdmin && (
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <div>
                  <div className="text-sm font-medium text-foreground">Share with the team</div>
                  <div className="text-xs text-muted-foreground">Everyone can use this view (admins only).</div>
                </div>
                <Switch checked={shared} onCheckedChange={setShared} data-testid="save-view-shared" />
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button onClick={() => { if (name.trim()) { saveView(name.trim(), shared); setSaveOpen(false); setName(""); setShared(false); } }} data-testid="save-view-confirm">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
